/**
 * Report wording — every sentence the customer's report can say about their attic.
 * Approved by Heemo 2026-10-13. Works offline: the rules in reportData.js pick the band from
 * what the inspector entered, and these templates fill in that customer's numbers.
 *
 * Edit the words freely. Each band is a list of bullets on the report.
 *
 * The rule: a section only appears when we found something there. When it does, it says what we
 * found, why it matters, and what fixes it — educational, never claiming a problem we didn't see.
 */

// "Sarah, we found…" — or "We found…" when there's no name
const hi = (first) => (first ? `${first}, we` : 'We');
const across = (sqft) => (sqft ? `across your ${sqft} sq ft attic` : 'across your attic floor');
const about = (coverage, fallback = 'part of') => (coverage ? `about ${coverage} of` : fallback);

/* ---------- 1. insulation depth — banded by estimated R-value ---------- */
// { first, depth, material, R, needed (total inches for R-60), more (inches to add), sqft }
export const DEPTH_BANDS = [
  { max: 1, id: 'none' },
  { max: 20, id: 'veryLow' },
  { max: 40, id: 'low' },
  { max: 60, id: 'close' },
  { max: Infinity, id: 'meets' },
];

export const DEPTH_TEXT = {
  none: ({ first, needed }) => [
    `${hi(first)} found no insulation on your attic floor.`,
    `The heat you pay for rises straight into the attic and out through the roof. Reaching R-60 takes about ${needed}" of insulation.`,
  ],
  veryLow: ({ first, depth, material, R, more, sqft }) => [
    `${hi(first)} found just ${depth}" of ${material} ${across(sqft)} — about R-${R}, less than a third of today's R-60 target.`,
    'Your home is losing a large share of its heat through the ceiling, and your furnace and A/C are working much harder than they should.',
    `Reaching R-60 takes about ${more}" more.`,
  ],
  low: ({ first, depth, material, R, more, sqft }) => [
    `${hi(first)} found ${depth}" of ${material} ${across(sqft)} — about R-${R}, well below the R-60 target.`,
    'Heat escapes in winter, and the rooms below run hotter in summer.',
    `Topping up with about ${more}" brings you to R-60.`,
  ],
  close: ({ first, depth, material, R, more }) => [
    `${hi(first)} found ${depth}" of ${material} — about R-${R}. You're closer than most homes we see, but still short of R-60.`,
    `A top-up of about ${more}" closes the gap.`,
  ],
  meets: ({ first, depth, material, R }) => [
    `${hi(first)} found ${depth}" of ${material} — about R-${R}, which meets the R-60 target.`,
    'Loose-fill insulation settles and compacts over the years, so it’s worth re-checking the depth every few years to make sure your home keeps that protection.',
  ],
  extraArea: ({ n, depth, material, R }) => `Attic area ${n}: ${depth}" of ${material}, about R-${R}.`,
};

/* ---------- 2. soffits & baffles ---------- */
export const SOFFIT_TEXT = {
  'Soffits blocked': [
    'Your soffit vents — where fresh air enters your attic — are blocked by insulation.',
    'Without that airflow, warm, moist air gets trapped up there, which can lead to mould, frost on the underside of the roof in winter, and shingles wearing out faster.',
  ],
  'Baffles needed': [
    'Your eaves need baffles. They hold the insulation back from the soffits so outside air can always flow into the attic.',
  ],
  'Baffles present': [
    'Your attic has some baffles in place.',
    'Adding more baffles improves ventilation — fresh air comes in at the soffits so built-up heat, moisture and pressure can leave the attic.',
  ],
};

/* ---------- 3. air sealing ---------- */
export const AIR_SEALING_TEXT = {
  'Gaps around penetrations': [
    'We found unsealed gaps where wiring, plumbing and the hatch pass through your ceiling.',
    'Warm air escapes through them all winter, carrying moisture into the attic. They should be sealed before any new insulation goes in.',
  ],
  'Pot light covers needed': [
    'Your pot lights need covers before insulation can go over them. The covers keep insulation a safe distance from the fixtures and stop warm air leaking around them.',
  ],
};

/* ---------- 5. bathroom exhaust ---------- */
export const EXHAUST_TEXT = {
  attic: [
    'Your bathroom fan vents into the attic instead of outside.',
    'Every shower sends warm, wet air up there, where it can condense on the roof sheathing and lead to mould. The fan should be ducted to the exterior.',
  ],
  exterior: [
    'Your bathroom fans vent to the outside — that’s how it should be.',
    'The duct running through the attic should be insulated and well sealed. An uninsulated duct in a cold attic collects condensation that can drip back down or soak into the insulation.',
  ],
  // "Could not confirm" / blank → no section
};

/* ---------- 4. attic hatch — shown when the hatch was photographed ---------- */
export const HATCH_TEXT = [
  'Your attic hatch is one of the most common heat leaks in a home.',
  'Weatherstripping it and insulating the cover to match the rest of the attic stops warm air escaping through it all winter.',
];

/* ---------- 6. mould — banded by level × coverage ---------- */
// { coverage } e.g. '50%'
export const MOULD_TEXT = {
  // "No" → no section
  early: ({ coverage }) => [
    `We found early-stage mould on ${about(coverage)} your roof sheathing.`,
    'It’s just starting, and this is the best time to deal with it — treating it now and fixing the moisture source stops it from spreading.',
  ],
  spreading: ({ coverage }) => [
    `We found surface mould across ${about(coverage, 'much of')} your roof sheathing.`,
    'It hasn’t set in deeply, but it has spread widely and should be addressed soon, along with the moisture feeding it.',
  ],
  serious: ({ coverage }) => [
    `We found active mould growth on ${about(coverage)} your roof sheathing.`,
    'It needs professional remediation before any new insulation goes in — otherwise it gets sealed in and keeps growing. Mould can affect your indoor air quality.',
  ],
  severe: ({ coverage }) => [
    `We found serious mould growth across ${about(coverage, 'most of')} your roof sheathing.`,
    'This should be addressed as soon as possible, before it spreads further.',
    'Left alone, it can affect your family’s health and indoor air quality, lead to structural damage, and lower your home’s value.',
  ],
  // mould found but no level picked yet
  unrated: ({ coverage }) => [`We found signs of mould on ${about(coverage)} your roof sheathing.`],
};

/* ---------- 7. pests — only on the report when found ---------- */
export const PEST_TEXT = [
  'We found signs of animal or pest activity in your attic.',
  'Droppings and nesting contaminate insulation and reduce how well it works. Contaminated insulation often needs to be removed before new insulation goes in.',
];
